import isToday from 'date-fns/isToday';
import isYesterday from 'date-fns/isYesterday';
import setHours from "date-fns/setHours";
import setMinutes from "date-fns/setMinutes";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Button, Col, Collapse, Form, InputGroup, Row } from 'react-bootstrap';
import StyledSelect from '../forms/styledSelect';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useReactToPrint } from 'react-to-print';
import { toast } from '../../utils/toast';
import ClientInfoModal from '../../pages/clients/clientInfo';
import SetClientModal from '../../pages/clients/clientsSet';
import SetLineItemModal from '../../pages/jobs/lineItemSet';
import { getAvailableBadges, getBadgesByIdList } from '../../services/badges';
import { tagLabel, ASSIGNMENT_LABELS, NOTIFICATION_TITLES } from '../../constants/labels';
import { getUsersAsOptions } from '../../services/companyUsers';
import { getJobActivities } from '../../services/jobActivities';
import { createJobNote, getJobNotes } from '../../services/jobNotes';
import { getNotificationsByJob } from '../../services/notifications';
import { deleteJob, getJobById, updateJob } from '../../services/jobs';
import { getClientStats } from '../../services/clients';
import { invalidateJobStats } from '../../utils/invalidateStats';
import { createLineItem, deleteLineItem, updateLineItem } from '../../services/lineItems';
import { getSaleInvoice } from '../../services/sales';
import { jobLabel, lineItemLabel } from '../../utils/constants';
import { getFullAddress, intlPhoneDigits, openViberChat, roundDecimal, rsdFormat, toLocalDateString, toTitleCase } from '../../utils/helper';
import { isCleaningAndOnwards, isCleaningCompletedAndOnwards, validateJobForm } from '../../utils/jobHelper';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import { isUserAdmin } from '../../utils/roleHelpers';
import DatePicker from '../forms/formDatePicker';
import FormInput from '../forms/formInput';
import OverlayLoader from '../overlayLoader';
import SalesInvoicePrint from '../print/salesInvoice';
import AssigneeSelect from '../selects/assigneeSelect';
import BadgeSelect from '../selects/badgeSelect';
import ClientsSelect from '../selects/clientsSelect';
import ServiceSelect from '../selects/serviceSelect';
import MaterialSelect from '../selects/materialSelect';
import StatusSelector from '../statusSelector';
import Avatar from '../avatar';
import SbrDrawer from '../sbr/SbrDrawer';
import ViberIcon from '../icons/viberIcon';
import ConfirmationModal from './confirmation';
import InlineAddressMap from './inlineAddressMap';
import JobNoteComposer from './jobNoteComposer';
import JobNotePhotoModal from './jobNotePhotoModal';
import useIsMobile from '../../hooks/useIsMobile';
import InvoiceSharePanel from '../invoiceSharePanel';
import JobDocumentsSection from '../documents/jobDocumentsSection';
import CustomerMessagePrompt from '../tracking/customerMessagePrompt';
import { JOB_STATUS, getStatusIndex, getStatusLabel, getStatusMeta, getSkippedStages, normalizeStatus, isCancelled } from '../../constants/jobStatus';

/* Activity timeline: the API stores enum codes (PAID, CASH, …) as the changed
   values; this maps each one to the translation key the form itself uses. */
const ACTIVITY_VALUE_KEYS = {
    paymentStatus: { PAID: 'common.paid', PENDING: 'common.pending' },
    pickupType: { PICKUP_FROM_HOME: 'orderForm.pickupFromHome', CUSTOMER_DELIVERS: 'orderForm.customerDelivers' },
    paymentMethod: { CASH: 'orderForm.cash', CREDIT_CARD: 'orderForm.creditCard', CHECK: 'orderForm.check' },
    paymentType: { BEFORE_SERVICE: 'orderForm.beforeService', DURING_SERVICE: 'orderForm.duringService', AFTER_SERVICE: 'orderForm.afterService' },
    type: { SERVICE: 'orderForm.service', MATERIAL: 'orderForm.material' },
}

/** First tag picked on more than one item of the form (label), or null. Tags are single-use. */
const findDuplicateTag = (lineItems, labelOf) => {
    const seen = new Set()
    for (const li of lineItems || []) {
        if (!li.badgeId) continue
        if (seen.has(li.badgeId)) return labelOf(li.badgeId) || li.badgeId
        seen.add(li.badgeId)
    }
    return null
}

/** PENDING payment whose due date has passed. */
const isPaymentOverdue = (job) => job?.paymentStatus === 'PENDING' && !!job?.paymentDueDate && new Date(job.paymentDueDate) < new Date()

const ClipboardIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
);
const MaterialIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
);
const ServiceIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
);

const initJob = {
    clientId: '',
    pickupType: 'PICKUP_FROM_HOME',
    pickupTime: null,
    pickupTimeTo: null,
    status: 'SCHEDULED',
    completionTime: null,
    completionTimeTo: null,
    paymentStatus: 'PENDING',
    paymentMethod: null,
    lineItemsTemp: [{
        quantity: 0,
        price: 0,
        taxRate: 0,
        taxAmount: 0,
        priceWithTax: 0,
        serviceId: '',
        badgeId: ''
    }],
    paymentType: null,
    paymentTime: null,
    pickupAssignedToId: null,
    cleaningAssignedToId: null,
    deliveryAssignedToId: null,
    note: ''
}

const SetTempEditOrderModal = ({
    display,
    toggle,
    jobId,
    setSelectedJob,
    alert,
    setAlert
}) => {

    const { t } = useTranslation()

    const [confirmationModal, setConfirmationModal] = useState(false)
    const [deleteJobConfirmation, setDeleteJobConfirmation] = useState(false)
    const [cancelJobConfirmation, setCancelJobConfirmation] = useState(false)
    const [resumeJobConfirmation, setResumeJobConfirmation] = useState(false)
    const [resumeToStatus, setResumeToStatus] = useState(null)
    const [cancelReason, setCancelReason] = useState('')
    const [setClientModal, setSetClientModal] = useState(false)
    const [editClientModal, setEditClientModal] = useState(false)
    const [setLineItemModal, setSetLineItemModal] = useState(false)
    const [clientInfoModal, setClientInfoModal] = useState(false)

    const [clientRawName, setClientRawName] = useState('')
    const [clientOptions, setClientOptions] = useState([])
    const [assigneesOptions, setAssigneesOptions] = useState([])
    const [isAdvance, setIsAdvance] = useState(true)
    const [showJobActivities, setShowJobActivities] = useState(false)
    const [availableBadges, setAvailableBadges] = useState([])
    const [selectedBadges, setSelectedBadges] = useState([])
    const [clientStats, setClientStats] = useState(null)
    // job-note photo currently opened full size from the timeline (null = closed)
    const [photoToView, setPhotoToView] = useState(null)

    const [job, setJob] = useState(initJob)
    const [isLoadingLineItemBadgeIds, setIsLoadingLineItemBadgeIds] = useState(false)
    const [lineItemBadges, setLineItemBadges] = useState([])
    //const [jobActivities, setJobActivities] = useState([])
    const [groupedJobActivities, setGroupedJobActivities] = useState([])
    const [lineItemToEdit, setLineItemToEdit] = useState()
    const [lineItemToDelete, setLineItemToDelete] = useState()
    const [lineItemToDeleteIndex, setLineItemToDeleteIndex] = useState()

    // index of the line item open in the right detail pane (null = show summary)
    const [detailIndex, setDetailIndex] = useState(null)
    const openItem = (index) => setDetailIndex(index)
    const closeItem = () => setDetailIndex(null)

    // mobile wizard
    const isMobile = useIsMobile()
    const WIZ_STEPS = [t('orderForm.stepClient'), t('orderForm.stepItems'), t('orderForm.stepStatus'), t('orderForm.stepTeam'), t('orderForm.stepSchedule'), t('orderForm.stepReview')]
    const [wizStep, setWizStep] = useState(0)
    const goWiz = (delta) => setWizStep((s) => Math.max(0, Math.min(WIZ_STEPS.length - 1, s + delta)))

    const [invoice, setInvoice] = useState()
    const [isPrinting, setIsPrinting] = useState(false)
    const [invoiceToSend, setInvoiceToSend] = useState(null)
    // Customer-facing message prompt (tracking): set to the canonical stage
    // just reached when a save moved the job to Confirmed / Scheduled / Ready
    // / Completed. The drawer closes once the prompt is dismissed.
    const [customerPromptStage, setCustomerPromptStage] = useState(null)
    const [trackingPromptManual, setTrackingPromptManual] = useState(false)
    const TEMPLATE_STAGES = ['CONFIRMED', 'SCHEDULED', 'READY', 'COMPLETED']
    const openTrackingSend = () => {
        setCustomerPromptStage(TEMPLATE_STAGES.includes(job?.status) ? job.status : 'CONFIRMED')
        setTrackingPromptManual(true)
    }
    const loadedStatusRef = useRef(null)
    const invoiceRef = useRef()

    const { data: dbJob, isFetching } = useQuery(['jobs', 'details', jobId], () => jobId && getJobById(jobId))
    const { data: dbJobActivities, isFetching: isFetchingJobActivities } = useQuery(['job-activities', jobId], () => jobId && getJobActivities(jobId))
    const { data: dbJobNotifications } = useQuery(['job-notifications', jobId], () => jobId && getNotificationsByJob(jobId))
    const { data: dbJobNotes } = useQuery(['job-notes', jobId], () => jobId && getJobNotes(jobId))
    const { data: dbAssigneesOptions, isLoading: isLoadingAssigneesOptions } = useQuery(['users-option'], () => getUsersAsOptions())
    const { data: dbAvailableBadges, isLoading: isAvailableBadgesLoading, isFetching: isAvailableBadgesFetching } = useQuery(['available-badges'], () => getAvailableBadges())
    const { data: dbSaleInvoice, isFetching: isFetchingSaleInvoice } = useQuery(['getSaleInvoice', jobId], () => jobId && getSaleInvoice(jobId))
    const { data: dbClientStats, isFetching: isClientStatsFetching } = useQuery(['client-stats', job.clientId], () => job.clientId && getClientStats(job.clientId), { enabled: !!job.clientId })

    // The stage the job was in right before it was cancelled, so the status
    // spine can freeze there instead of resetting to the first stage.
    const cancelledFromStatus = useMemo(() => {
        // Every API response is wrapped as { code, message } — the activities are
        // in `message`. Testing Array.isArray on the wrapper itself is always
        // false, which silently returned null here forever: the spine then froze
        // at normalizeStatus(null) === SCHEDULED regardless of the real stage.
        const activities = Array.isArray(dbJobActivities?.message) ? dbJobActivities.message : null
        if (!isCancelled(job.status) || !activities) return null
        let latest = null
        for (const activity of activities) {
            for (const u of (activity.message?.updates || [])) {
                if (u.prop === 'status' && normalizeStatus(u.to) === JOB_STATUS.CANCELLED) {
                    if (!latest || new Date(activity.createdAt) > new Date(latest.createdAt))
                        latest = { createdAt: activity.createdAt, from: u.from }
                }
            }
        }
        return latest?.from ?? null
    }, [job.status, dbJobActivities])

    useEffect(() => {

        if (!dbAvailableBadges || !dbAssigneesOptions || !dbJobActivities || !dbJob)
            return
        // dbJobNotifications is allowed to still be loading/absent — the merged
        // timeline just shows activities-only until it arrives.

        onQuerySuccess(dbAvailableBadges, (result) => {
            if (result.length > 0) {
                setAvailableBadges(result.map(d => ({
                    value: d.id, label: tagLabel(d)
                })))
            }
        })

        onQuerySuccess(dbAssigneesOptions, (result) => {
            if (result.length > 0) {
                setAssigneesOptions(result.map(d => ({
                    value: d.id, label: d.fullName, roles: d.roles, avatarUrl: d.avatarUrl
                })))
            }
        })

        onQuerySuccess(dbJobActivities, (result) => {
            // Creating a note writes a JobNote row *and* a mirrored activity row
            // (message.kind === 'note') for the exact same event. The JobNote is
            // the canonical record — stable id, authoritative body/photos, full
            // author incl. avatarUrl — so it is the one rendered, and the mirror
            // is dropped here; rendering both would double every note.
            const activities = result.filter(a => a.message?.kind !== 'note').map(a => ({ ...a, _kind: 'activity' }))
            // Notifications are a separate system on the backend (no schema
            // change) — merged here purely for display, into the same
            // chronological timeline the client asked for.
            const notifications = Array.isArray(dbJobNotifications?.message)
                ? dbJobNotifications.message.map(n => ({ ...n, _kind: 'notification' }))
                : []
            const notes = Array.isArray(dbJobNotes?.message)
                ? dbJobNotes.message.map(n => ({ ...n, _kind: 'note' }))
                : []
            let _groupedActivities = groupActivitiesByDateCreated([...activities, ...notifications, ...notes])
            setGroupedJobActivities(_groupedActivities)
            //setJobActivities(result)
        })

        onQuerySuccess(dbJob, (result) => {
            loadedStatusRef.current = result?.status ?? null
            setIsAdvance(true)
            setClientOptions([{
                value: result.clientId,
                label: result.client.clientType === 'COMPANY' && result.client.companyName
                    ? result.client.companyName
                    : `${result.client.firstName} ${result.client.lastName}`
            }])
            createJobLineItemsTemp(result)

            const _lineItemBadgeIds = result.lineItems.map(x => x.badgeId)
            if (_lineItemBadgeIds.length > 0) {

                const fetchBadges = async () => {

                    let _lineItemBadges = []
                    setIsLoadingLineItemBadgeIds(true)

                    const { code, message } = await getBadgesByIdList(_lineItemBadgeIds)
                    if (code === 200)
                        _lineItemBadges = message.map(x => { return { value: x.id, label: tagLabel(x) } })

                    setLineItemBadges(_lineItemBadges)
                }

                fetchBadges()
                    .then(() => { setIsLoadingLineItemBadgeIds(false) })
                    .catch(console.error);
            } else {
                setLineItemBadges([])
            }

        })

    }, [dbAvailableBadges, dbAssigneesOptions, dbJobActivities, dbJobNotifications, dbJobNotes, dbJob])

    const groupActivitiesByDateCreated = (activities) => {

        const groupedActivities = activities.reduce((group, activity) => {
            const { createdAt } = activity;
            const createdAtDate = toLocalDateString(new Date(createdAt), 'yyyy-MM-dd')

            group[createdAtDate] = group[createdAtDate] ?? [];
            group[createdAtDate].push(activity);
            return group;
        }, {});

        const groupedActivitiesArray = Object.keys(groupedActivities).map((createdAtDate) => {
            return {
                createdAtDate,
                // Merging activities + notifications means items no longer arrive
                // pre-interleaved by time — sort within the day so it reads as one
                // true chronological history rather than two separate blocks.
                activities: groupedActivities[createdAtDate].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            };
        });

        const sortedGroupedActiviesArray = groupedActivitiesArray.sort(function (a, b) { return new Date(b.createdAtDate) - new Date(a.createdAtDate) })
        return sortedGroupedActiviesArray
    }

    const createJobLineItemsTemp = (result = job) => {
        const lineItemsTemp = result.lineItems.map(x => {
            const isMat = x.type === 'MATERIAL'
            return {
                ...x,
                pricePerUnit: isMat ? parseFloat(x.material?.sellingPrice ?? 0) : parseFloat(x.service?.pricePerUnit ?? 0),
                unit: isMat ? (x.material?.unit ?? '') : (x.service?.unit ?? ''),
                materialName: isMat ? (x.material?.name ?? '') : undefined,
                serviceName: !isMat ? (x.service?.name ?? '') : undefined,
            }
        })

        let _dbJob = { ...result, lineItemsTemp };
        _dbJob = { ..._dbJob, ...{ clientId: result.clientId } };

        const _selectedBadges = _dbJob.lineItemsTemp.map(x => x.badgeId)
        setSelectedBadges(_selectedBadges)

        console.log(_dbJob)
        setJob(_dbJob)
    }

    const queryClient = useQueryClient()

    const CUSTOMER_MESSAGE_STAGES = [JOB_STATUS.CONFIRMED, JOB_STATUS.SCHEDULED, JOB_STATUS.READY, JOB_STATUS.COMPLETED]

    const { mutate, isLoading } = useMutation(updateJob, {
        onSuccess: onMutateSuccess((saved) => {
            invalidateJobStats(queryClient)
            queryClient.invalidateQueries(['jobs', 'details', jobId])
            queryClient.invalidateQueries(['job-activities', jobId])
            queryClient.invalidateQueries(['available-badges'])
            queryClient.invalidateQueries(['getSaleInvoice', jobId])
            const newStatus = normalizeStatus(saved?.status)
            const oldStatus = loadedStatusRef.current && normalizeStatus(loadedStatusRef.current)
            if (saved?.status && newStatus !== oldStatus && CUSTOMER_MESSAGE_STAGES.includes(newStatus)) {
                loadedStatusRef.current = saved.status
                setCustomerPromptStage(newStatus)
                return
            }
            onHide()
        })
    })

    const { mutate: mutateDeleteJob, isLoading: isDeletingJob } = useMutation(deleteJob, {
        onSuccess: onMutateSuccess((result) => {
            invalidateJobStats(queryClient)
            toggle(false)
        }, isUserAdmin() ? t('jobs.jobDeletedSuccess') : t('jobs.jobDeleteRequestSent'))
    })

    const { mutate: mutateDeleteLineItem, isLoading: isDeletingLineItem } = useMutation(deleteLineItem, {
        onSuccess: onMutateSuccess((result, id) => {
            invalidateJobStats(queryClient)
            queryClient.invalidateQueries(['jobs', 'details', jobId])
            queryClient.invalidateQueries(['available-badges'])
            queryClient.invalidateQueries(['getSaleInvoice', jobId])
            closeItem()
        }, t('orderForm.lineItemDeleted'))
    })

    const onHide = () => {
        setIsAdvance(false)
        alert && setAlert(false)
        //setSelectedJob && setSelectedJob({})
        //setJob(initJob)
        toggle(false)
    }

    const onShow = () => {
        setDetailIndex(null)
        setWizStep(0)
    }

    const updateLineItems = async (lineItems) => {
        const promises = []
        for (const element of lineItems) {
            if (element.isTouched) {
                if (element.id) {
                    // await mutateUpdateLineItem({ ...element, jobId });
                    promises.push(updateLineItem({ ...element, jobId }));
                } else {
                    // await mutateCreateLineItem({ ...element, jobId });
                    promises.push(createLineItem({ ...element, jobId }));
                }
            }
        }
        return Promise.all(promises)
    };


    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        const validationError = validateJobForm(job)
        if (validationError) {
            toast.error(validationError, { position: toast.POSITION.BOTTOM_RIGHT })
            return
        }

        const duplicateTag = findDuplicateTag(job.lineItemsTemp, badgeLabel)
        if (duplicateTag) {
            toast.error(t('orderForm.duplicateTag', { tag: duplicateTag }), { position: toast.POSITION.BOTTOM_RIGHT })
            return
        }

        let _job = { ...job }
        _job.price = parseFloat(getTotalPriceRaw())

        if (normalizeStatus(_job.status) === JOB_STATUS.READY && !_job.dispatchTime)
            _job.dispatchTime = new Date().toISOString()

        if (!_job.pickupAssignedToId) {
            _job.pickupAssignedToId = null
            _job.pickupAssignedTo = null
        }
        if (!_job.cleaningAssignedToId) {
            _job.cleaningAssignedToId = null
            _job.cleaningAssignedTo = null
        }
        if (!_job.deliveryAssignedToId) {
            _job.deliveryAssignedToId = null
            _job.deliveryAssignedTo = null
        }

        mutate({ ..._job, updateLineItems })
    }

    const handleChange = (objValue) => {

        const newOrder = { ...job, ...objValue }
        if (newOrder.pickupType === "CUSTOMER_DELIVERS") {
            newOrder.pickupTime = null
            newOrder.pickupTimeTo = null
        }
        if (newOrder.pickupType === "PICKUP_FROM_HOME") {
            newOrder.deliveredByCustomerTime = null
            newOrder.deliveredByCustomerTimeTo = null
        }

        setJob(newOrder)
    }

    const handleAddLineItem = () => {
        let _order = job
        let _lineItems = job.lineItemsTemp
        // Tags are single-use, so a new item never inherits one: the editor
        // offers the next free tag as a one-click chip instead.
        const badgeId = ''
        _lineItems.push({
            type: 'SERVICE',
            quantity: 0,
            price: 0,
            taxRate: 0,
            taxAmount: 0,
            priceWithTax: 0,
            serviceId: '',
            badgeId
        })
        _order.lineItemsTemp = _lineItems
        setJob({ ..._order })
        setSelectedBadges(_lineItems.map(x => x.badgeId))
        // open the freshly added item in the right detail pane
        openItem(_lineItems.length - 1)
    }

    // Copies every field of an existing item into a new one placed right after
    // it — `id` is dropped so it saves as a new line item, not an update to
    // the original (see updateLineItems: no `id` means create).
    const handleDuplicateLineItem = (index) => {
        let _order = job
        // The copy never carries the tag over — a tag belongs to exactly one item.
        const { id, badge, ...rest } = job.lineItemsTemp[index]
        const _lineItems = [...job.lineItemsTemp]
        _lineItems.splice(index + 1, 0, { ...rest, badgeId: '', isTouched: true })
        _order.lineItemsTemp = _lineItems
        setJob({ ..._order })
        setSelectedBadges(_lineItems.map(x => x.badgeId))
        openItem(index + 1)
    }

    const handleLineItemChange = (objValue, index) => {

        const _lineItems = [...job.lineItemsTemp]; // Create a new copy of the array
        _lineItems[index] = { ..._lineItems[index], ...objValue }

        let _quantity = _lineItems[index].quantity

        if (_lineItems[index].unit === 'm2') {
            const length = +_lineItems[index].length || 0
            const width = +_lineItems[index].width || 0
            _quantity = roundDecimal(length * width, 2)
        }

        _lineItems[index].quantity = _quantity

        let _price = +_lineItems[index].pricePerUnit * +_lineItems[index].quantity
        _lineItems[index].price = roundDecimal(_price, 2)

        let _taxAmount = (+_lineItems[index].taxRate / 100) * +_lineItems[index].price
        _lineItems[index].taxAmount = roundDecimal(_taxAmount, 2)

        let _priceWithTax = +_lineItems[index].price + +_lineItems[index].taxAmount
        _lineItems[index].priceWithTax = roundDecimal(_priceWithTax, 2)

        _lineItems[index].isTouched = true

        const _selectedBadges = _lineItems.map(x => x.badgeId)
        setSelectedBadges(_selectedBadges)

        setJob((prevJob) => ({
            ...prevJob,
            lineItemsTemp: _lineItems // Update the state with the new array
        }));
    }

    const getTotalPriceRaw = () => {
        var sum = job.lineItemsTemp?.reduce(function (acc, obj) {
            return parseFloat(acc) + parseFloat(obj.priceWithTax);
        }, 0);
        return sum;
    }

    const getTotalPrice = () => {
        var sum = job.lineItemsTemp?.reduce(function (acc, obj) {
            return parseFloat(acc) + parseFloat(obj.priceWithTax);
        }, 0);
        return rsdFormat(sum)
    }

    const onCreateClient = (newClient) => {

        let _clientOptions = clientOptions
        _clientOptions.push({
            value: newClient.id,
            label: newClient.name
        })

        let _job = job
        _job.clientId = newClient.id
        setJob({ ..._job })
    }

    const onUpdateClient = (updatedClient) => {

        const label = updatedClient?.name || `${updatedClient?.firstName ?? ''} ${updatedClient?.lastName ?? ''}`.trim()
        if (label)
            setClientOptions(clientOptions.map(x => x.value === job.clientId ? { ...x, label } : x))

        queryClient.invalidateQueries(['client-stats', job.clientId])
    }

    const handlePaymentStatusChange = (checked) => {
        handleChange({ paymentStatus: checked ? 'PAID' : 'PENDING' })
    }

    const handleServiceSelect = (option, index) => {
        handleLineItemChange({ serviceId: option.value, serviceName: option.label, pricePerUnit: parseFloat(option.ppu), unit: option.unit, quantity: 0 }, index)
    }

    const handleMaterialSelect = (option, index) => {
        handleLineItemChange({
            materialId: option.value,
            materialName: option.label,
            pricePerUnit: parseFloat(option.ppu),
            unit: option.unit,
            taxRate: parseFloat(option.taxRate) || 0,
            quantity: 1,
        }, index)
    }

    const handleAddMaterialLineItem = () => {
        let _order = job
        let _lineItems = job.lineItemsTemp
        _lineItems.push({
            type: 'MATERIAL',
            quantity: 1,
            price: 0,
            taxRate: 0,
            taxAmount: 0,
            priceWithTax: 0,
            materialId: '',
            badgeId: ''
        })
        _order.lineItemsTemp = _lineItems
        setJob({ ..._order })
        setSelectedBadges(_lineItems.map(x => x.badgeId))
        openItem(_lineItems.length - 1)
    }

    const ACTIVITY_DATE_TIME_FORMAT = 'dd.MM.yyyy  hh:mm a'
    const toActivityDateString = (date) => toLocalDateString(date, ACTIVITY_DATE_TIME_FORMAT)

    // "created the job", "updated a line item", … — the API stores the action
    // (create | update | delete) and the object ('Job' | 'Line Item') as codes.
    const activityVerb = (message) => {
        const isJob = message?.object === 'Job'
        switch (message?.action) {
            case 'create': return isJob ? t('orderForm.createdJob') : t('orderForm.createdLineItem')
            case 'update': return isJob ? t('orderForm.updatedJob') : t('orderForm.updatedLineItem')
            case 'delete': return isJob ? t('orderForm.deletedJob') : t('orderForm.deletedLineItem')
            default: return `${message?.action}d ${message?.object}`
        }
    }

    const getActivityActionMessage = (activity) => {

        return <>
            <span className='fw-bold'>{activity.createdBy?.fullName ?? t('orderForm.system')}</span>
            {` ${t('orderForm.activityAt', { action: activityVerb(activity.message), date: toActivityDateString(activity.createdAt) })}`}
        </>
    }

    // Keyed on the field code rather than its label: the label is translated,
    // so comparing it to "Status" only worked in English.
    const TONE_TO_TEXT_CLS = { grey: 'wf-text-grey', amber: 'wf-text-amber', green: 'wf-text-green', cancelled: 'wf-text-cancelled' }
    const getVariantIfStatusProp = (prop, progress) => {
        if (prop === 'status') return TONE_TO_TEXT_CLS[getStatusMeta(progress).tone]
    }

    const getActivityUpdateMessage = (update, object) => {
        const objectLabels = object === 'Job' ? jobLabel : lineItemLabel
        const { prop, from, to } = update
        const skipped = prop === 'status' ? getSkippedStages(from, to) : []

        return <>
            <Trans i18nKey="orderForm.activityChange"
                values={{ field: objectLabels[prop] || toTitleCase(prop), from: formatValByProp(from, prop), to: formatValByProp(to, prop) }}
                components={{
                    field: <small className='fw-bold' />,
                    from: <small className={`fw-bold ${getVariantIfStatusProp(prop, from)}`} />,
                    to: <small className={`fw-bold ${getVariantIfStatusProp(prop, to)}`} />,
                }} />
            {skipped.length > 0 && <small className='wf-skipped'>{` (${t('orderForm.skippedStages', { stages: skipped.join(', ') })})`}</small>}
        </>
    }

    const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
    const DATE_TIME_PROPS = ["completionTimeActual", "dispatchTime", "pickupTime", "pickupTimeTo", "completionTime", "completionTimeTo", "pickupInProgressTime",
        "cleaningTime", "cleaningCompletedTime", "deliveredByCustomerTime", "deliveredByCustomerTimeTo", "pickupCompletedTime", "dispatchInProgressTime", "paymentTime"]

    const formatValByProp = (val, prop) => {
        if (prop === "deliveryAssignedToId" || prop === "pickupAssignedToId" || prop === "cleaningAssignedToId")
            return val?.fullName ?? t('jobs.unassigned')
        else if (DATE_TIME_PROPS.includes(prop) || (typeof val === 'string' && ISO_DATE_RE.test(val)))
            return val ? toActivityDateString(val) : t('orderForm.noTimeSet')
        else if (prop === "serviceId")
            return val?.name
        else if (prop === "clientId")
            return `${val?.firstName} ${val?.lastName}`
        else if (prop === "status")
            return getStatusLabel(val)
        else if (ACTIVITY_VALUE_KEYS[prop]?.[val])
            return t(ACTIVITY_VALUE_KEYS[prop][val])
        else
            return val
    }

    const handleShowConfirmation = (lineItem, index) => {
        setConfirmationModal(true)
        setLineItemToDelete(lineItem)
        setLineItemToDeleteIndex(index)
    }

    const handleDeleteLineItem = () => {
        if (lineItemToDelete.id)
            mutateDeleteLineItem(lineItemToDelete.id)
        else {
            const lineItems = job.lineItemsTemp.filter((item, index) => index !== lineItemToDeleteIndex)
            setJob({
                ...job,
                lineItemsTemp: lineItems
            })
        }
        closeItem()
    }

    const handleSelectDate = (prop, date) => {

        let _date = date

        const dateObj = new Date(date)
        //check if midnight, means no time is selected then set to 8:00 as default
        if (dateObj.getHours() === 0 && dateObj.getMinutes() === 0 && dateObj.getSeconds() === 0) {
            _date = setHours(setMinutes(date, 0), 8)
        }

        handleChange({ [prop]: _date })
    }

    const activityHeaderText = (date) => {
        if (isToday(new Date(date)))
            return t('common.today')
        if (isYesterday(new Date(date)))
            return t('common.yesterday')
        return toLocalDateString(new Date(date), 'dd.MM.yyyy')
    }

    const getLineItemMinQuantity = (lineItemUnit) => {
        if (isCleaningCompletedAndOnwards(job)) {
            return lineItemUnit === 'Piece' ? 1 : 0.01
        } else {
            return 0
        }
    }

    const handleDeleteJob = () => {
        mutateDeleteJob(job.id)
    }

    // Cancel is not a stepper stage — it's a direct, one-step action settable
    // from any stage, same PATCH mechanism as any other status change.
    //
    // The reason rides along as a job note rather than a field on the job: the
    // note log is append-only, so a later cancellation can never overwrite the
    // record of an earlier one, and it lands in the same timeline as the status
    // change itself. Written BEFORE the PATCH so a failed status write can't
    // leave a reason explaining a cancellation that never happened.
    const askCancelJob = () => {
        setCancelReason('')
        setCancelJobConfirmation(true)
    }

    const handleCancelJob = async () => {
        const reason = cancelReason.trim()
        if (reason) {
            const response = await createJobNote(job.id, { note: `Cancellation reason: ${reason}`, photos: [] })
            if (response?.code !== 200) {
                toast.error(response?.message || t('orderForm.cancelReasonError'), { position: toast.POSITION.BOTTOM_RIGHT })
                return
            }
            queryClient.invalidateQueries(['job-notes', jobId])
        }
        mutate({ id: job.id, status: JOB_STATUS.CANCELLED })
    }

    // Resume — the inverse of cancel, and the same PATCH. The target always
    // comes from StatusSelector, so it matches the stage the frozen spine shows
    // as reached; when no cancellation was recorded in the history that is
    // whatever normalizeStatus falls back to, which is what the spine drew
    // anyway. Either way the confirmation names the stage before committing.
    const askResumeJob = (status) => {
        setResumeToStatus(normalizeStatus(status) || JOB_STATUS.NEW)
        setResumeJobConfirmation(true)
    }

    const handleResumeJob = () => {
        mutate({ id: job.id, status: resumeToStatus || JOB_STATUS.NEW })
    }

    const printInvoice = useReactToPrint({
        content: () => invoiceRef.current,
        onAfterPrint: () => {
            setIsPrinting(false);
        }
    });

    const handlePrint = async () => {

        setIsPrinting(true)
        printInvoice()
    }

    useEffect(() => {
        onQuerySuccess(dbSaleInvoice, (result) => {
            setInvoice(result)
        })
    }, [dbSaleInvoice]);

    useEffect(() => {
        onQuerySuccess(dbClientStats, (result) => setClientStats(result))
    }, [dbClientStats]);

    const allBadgeOptions = [...availableBadges, ...lineItemBadges]
    const badgeLabel = (badgeId) => allBadgeOptions.find(b => b.value === badgeId)?.label
    const sortBadgeOptions = (options) => [...options].sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true }))
    // The lowest free tag not already picked on this form — the one-click "Next" suggestion.
    const nextFreeBadge = sortBadgeOptions(availableBadges).find(b => !selectedBadges.includes(b.value))
    const renderNextTagChip = (lineItem, index, disabled) => (nextFreeBadge && lineItem.badgeId !== nextFreeBadge.value && !disabled) ? (
        <button type="button" className="rf" title={t('orderForm.useNextFreeTag')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px', padding: '2px 8px', borderRadius: '12px', border: '1px solid var(--brand-100)', background: 'var(--brand-50)', color: 'var(--brand-ink)', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
            onClick={() => handleLineItemChange({ badgeId: nextFreeBadge.value }, index)}>
            <i className="fa-solid fa-tag"></i> {t('orderForm.nextTag', { label: nextFreeBadge.label })}
        </button>
    ) : null

    /* compact, clickable line-item card (left column) */
    const renderItemCard = (lineItem, index) => {
        const isOn = detailIndex === index
        const isMaterial = lineItem.type === 'MATERIAL'
        const name = isMaterial
            ? (lineItem.materialName || lineItem.material?.name || (lineItem.materialId ? t('orderForm.selectedMaterial') : null))
            : (lineItem.serviceName || lineItem.service?.name || (lineItem.serviceId ? t('orderForm.selectedService') : null))
        const bl = badgeLabel(lineItem.badgeId)
        const meta = (isMaterial ? lineItem.materialId : lineItem.serviceId)
            ? `${rsdFormat(+lineItem.quantity, false)} ${lineItem.unit || ''}${bl ? ` · ${t('orderForm.tag')} ${bl}` : ''}`
            : (isMaterial ? t('orderForm.tapToChooseMaterial') : t('orderForm.tapToChooseService'))
        return (
            <button type="button" key={`li-pick-${index}`} className={`li-pick${isOn ? ' on' : ''}`} onClick={() => openItem(index)}>
                <span className="li-pick-ic">{isMaterial ? <MaterialIcon /> : <ServiceIcon />}</span>
                <span className="li-pick-mid">
                    <span className={`li-pick-nm${name ? '' : ' ph'}`}>
                        {name || t('orderForm.itemN', { n: index + 1 })}
                        {lineItem?.service?.disabled && <i className="fa-solid fa-triangle-exclamation text-danger ms-1" title={t('orderForm.serviceDisabled')}></i>}
                    </span>
                    <span className="li-pick-meta">{meta}</span>
                </span>
                <span className="li-pick-tot">{rsdFormat(+lineItem.priceWithTax, false)}</span>
                <span className="li-pick-chev">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </span>
            </button>
        )
    }

    /* right column — totals + status summary */
    const renderSummary = () => (
        <div className="job-pane summary" key="summary">
            <div className="d-flex justify-content-between align-items-center">
                <div className="job-sum-ttl">{t('orderForm.summary')}</div>
                {!isCancelled(job.status) &&
                    <button type="button" className="btn-sbr btn-sm-sbr btn-danger-sbr" onClick={askCancelJob}>
                        <i className="fa-solid fa-ban"></i> {t('orderForm.cancelJob')}
                    </button>
                }
            </div>
            <Form.Label>{t('orderForm.status')} <span className="text-danger">*</span></Form.Label>
            <StatusSelector value={job.status} onChange={(status) => handleChange({ status: status })}
                cancelledFromStatus={cancelledFromStatus} onResume={askResumeJob} />
            <div className="job-totals">
                <div className="tl"><span>{t('orderForm.items')}</span><span>{job.lineItemsTemp?.length || 0}</span></div>
                <div className="grand"><span className="l">{t('orderForm.grandTotal')}</span><span className="v">{getTotalPrice()}</span></div>
            </div>
        </div>
    )

    /* right column — single line-item detail editor */
    const renderItemEditor = (index) => {
        const lineItem = job.lineItemsTemp?.[index]
        if (!lineItem) return null
        const disabled = lineItem?.service?.disabled || lineItem?.material?.disabled
        const isMaterial = lineItem.type === 'MATERIAL'
        return (
            <div className="job-pane detail li-detail" key={`detail-${index}`}>
                <div className="li-detail-head">
                    <button type="button" className="li-detail-back" title={t('orderForm.backToSummary')} onClick={closeItem}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                    </button>
                    <div className="tx">
                        <div className="t">{isMaterial ? t('orderForm.material') : t('orderForm.lineItemN', { n: index + 1 })}</div>
                        <div className="s">{isMaterial ? t('orderForm.editMaterialAndPricing') : t('orderForm.editServiceAndPricing')}</div>
                    </div>
                </div>
                <div className="li-grid">
                    <div className="li-col-2">
                        {isMaterial ? (
                            <Form.Group>
                                <Form.Label>{t('orderForm.material')} <span className="text-danger">*</span></Form.Label>
                                <MaterialSelect materialId={lineItem?.materialId} required value={lineItem.materialId ?? ''} onChange={(option) => handleMaterialSelect(option, index)}
                                    isDisabled={disabled} />
                            </Form.Group>
                        ) : (
                            <Form.Group>
                                <Form.Label>{t('orderForm.service')} <span className="text-danger">*</span></Form.Label>
                                <ServiceSelect serviceId={lineItem?.serviceId} required value={lineItem.serviceId ?? ''} onChange={(option) => handleServiceSelect(option, index)}
                                    isDisabled={disabled} />
                            </Form.Group>
                        )}
                    </div>
                    <div className="li-col-2">
                        <Form.Group>
                            <Form.Label>{t('orderForm.tagNumber')} {isCleaningAndOnwards(job) && <span className="text-danger">*</span>}</Form.Label>
                            <InputGroup className="input-group-react-select">
                                <BadgeSelect required={isCleaningAndOnwards(job)} value={lineItem.badgeId ?? ''}
                                    isLoading={isAvailableBadgesLoading || isLoadingLineItemBadgeIds}
                                    options={sortBadgeOptions(allBadgeOptions
                                        .filter(x => !selectedBadges.includes(x.value) || x.value === lineItem.badgeId))}
                                    onChange={(option) => handleLineItemChange({ badgeId: option.value }, index)}
                                    isDisabled={disabled} />
                            </InputGroup>
                            {renderNextTagChip(lineItem, index, disabled)}
                        </Form.Group>
                    </div>
                    {lineItem.unit === 'm2' &&
                        <>
                            <div>
                                <FormInput value={lineItem.width ?? ''} label={t('orderForm.width')} type="number" min={0.01} step="0.01"
                                    onChange={(e) => handleLineItemChange({ width: e.target.value }, index)}
                                    required={isCleaningCompletedAndOnwards(job)} disabled={disabled} />
                            </div>
                            <div>
                                <FormInput value={lineItem.length ?? ''} label={t('orderForm.length')} type="number" min={0.01} step="0.01"
                                    onChange={(e) => handleLineItemChange({ length: e.target.value }, index)}
                                    required={isCleaningCompletedAndOnwards(job)} disabled={disabled} />
                            </div>
                        </>
                    }
                    <div>
                        <FormInput value={lineItem.quantity} title={rsdFormat(+lineItem.quantity, false)}
                            label={(lineItem.serviceId || lineItem.materialId) ? lineItem.unit : t('orderForm.quantity')}
                            type="number" min={getLineItemMinQuantity(lineItem.unit)} step={lineItem.unit === 'Piece' ? 1 : 0.01}
                            onChange={(e) => handleLineItemChange({ quantity: e.target.value }, index)}
                            disabled={lineItem.unit === 'm2' || disabled} required />
                    </div>
                    <div>
                        <FormInput value={lineItem.taxRate} label={t('orderForm.taxPercent')} type="number" step='0.01'
                            onChange={(e) => handleLineItemChange({ taxRate: e.target.value }, index)} disabled={disabled} />
                    </div>
                    <div>
                        <FormInput disabled value={lineItem.price} title={rsdFormat(+lineItem.price, false)}
                            label={t('orderForm.price')} type="number" min="0.01" step="0.01"
                            onChange={(e) => handleLineItemChange({ price: e.target.value }, index)} required />
                    </div>
                    <div>
                        <FormInput disabled value={lineItem.priceWithTax} title={rsdFormat(+lineItem.priceWithTax, false)}
                            label={t('orderForm.total')} type="number" min="0.01" step="0.01" required
                            onChange={(e) => handleLineItemChange({ priceWithTax: e.target.value }, index)} />
                    </div>
                </div>
                <div className="li-detail-total">
                    <span className="l">{t('orderForm.lineTotal')}</span>
                    <span className="v">{rsdFormat(+lineItem.priceWithTax, false)} RSD</span>
                </div>
                <div className="li-detail-actions">
                    <button type="button" className="li-detail-duplicate" onClick={() => handleDuplicateLineItem(index)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                        {t('orderForm.duplicateItem')}
                    </button>
                    <button type="button" className="li-detail-remove" onClick={() => handleShowConfirmation(lineItem, index)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                        {t('orderForm.removeItem')}
                    </button>
                </div>
            </div>
        )
    }

    /* current client details card — contact, address & stats for the job's client */
    const renderClientSummary = () => {
        if (!job.clientId) return null
        if (isClientStatsFetching && !clientStats) return <Col xs="12"><div className="jwc-card jwc-skel">{t('orderForm.loadingClient')}</div></Col>
        if (!clientStats?.client) return null
        const c = clientStats.client
        const isCompany = c.clientType === 'COMPANY'
        const contactName = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()
        const name = isCompany ? (c.companyName || contactName) : contactName
        const phoneDigits = intlPhoneDigits(c.phone)
        const address = getFullAddress(c)
        return (
            <Col xs="12">
                <div className="jwc-card">
                    <div className="jwc-top">
                        <Avatar square size={44} name={name} src={c.avatarUrl} />
                        <div className="jwc-id">
                            <div className="jwc-nm">{name}<span className="jwc-badge">{isCompany ? t('orderForm.company') : t('orderForm.individual')}</span><span className="jwc-badge">{clientStats.totalJobs > 0 ? t('orderForm.active') : t('orderForm.new')}</span></div>
                            {isCompany && contactName && <div className="jwc-phone">{t('orderForm.contact')} {contactName}</div>}
                            {c.phone && <div className="jwc-phone">{c.phone}</div>}
                        </div>
                        <div className="jwc-actions">
                            {phoneDigits &&
                                <a className="jwc-ic wa" href={`https://wa.me/${phoneDigits}`} target="_blank" rel="noreferrer" title="WhatsApp">
                                    <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.892c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652a11.94 11.94 0 0 0 5.71 1.454h.006c6.585 0 11.946-5.359 11.949-11.945a11.86 11.86 0 0 0-3.481-8.408" /></svg>
                                </a>}
                            {phoneDigits &&
                                <a className="jwc-ic vb" href={`viber://chat/?number=%2B${phoneDigits}`} onClick={(e) => { e.preventDefault(); openViberChat(c.phone); }} title="Viber">
                                    <ViberIcon />
                                </a>}
                            {c.phone &&
                                <a className="jwc-ic" href={`tel:${c.phone}`} title={t('clients.call')}>
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                                </a>}
                        </div>
                    </div>
                    {/* Address text + inline map strip. A cancelled job keeps its
                        location visible (history), just visually stood down. */}
                    <InlineAddressMap address={address} clientId={c.id} latitude={c.latitude} longitude={c.longitude}
                        cancelled={isCancelled(job.status)} />
                    <div className="jwc-stats">
                        <div className="jwc-stat"><div className="l">{t('orderForm.totalJobs')}</div><div className="v">{clientStats.totalJobs}</div></div>
                        <div className="jwc-stat"><div className="l">{t('orderForm.lastJob')}</div><div className="v">{clientStats.lastJob ? toLocalDateString(clientStats.lastJob.date, 'dd.MM.yyyy') : '—'}</div></div>
                        <div className="jwc-stat"><div className="l">{t('orderForm.lifetimeValue')}</div><div className="v">{rsdFormat(clientStats.totalRevenue)}</div></div>
                    </div>
                    <div className="jwc-foot">
                        <div className={`jwc-out${clientStats.unpaidAmount > 0 ? ' due' : ''}`}>
                            <span className="l">{t('orderForm.outstanding')}</span><span className="v">{rsdFormat(clientStats.unpaidAmount)}</span>
                        </div>
                        <div className="jwc-act">
                            <button type="button" className="jwc-view" onClick={() => setEditClientModal(true)}>{t('orderForm.editClient')}</button>
                            <button type="button" className="jwc-view" onClick={() => setClientInfoModal(true)}>{t('orderForm.viewClient')}</button>
                        </div>
                    </div>
                </div>
            </Col>
        )
    }

    /* ── field groups (shared by desktop split + mobile wizard) ── */
    const fClient = () => (
        <Col xs="12">
            <Row className="mb-2">
                <Col lg="12">
                    <div className="d-flex justify-content-between">
                        <Form.Label>{t('orderForm.client')} <span className="text-danger">*</span></Form.Label>
                        <div>
                            <span className="me-2 text-muted"><small>{t('orderForm.clientNotFound')}</small></span>
                            <Button style={{ fontSize: '0.70rem' }} className="mb-1 py-0 px-2" size="sm" variant="light" onClick={() => setSetClientModal(true)}><i className="fas fa-plus"></i> {t('orderForm.addNew')}</Button>
                        </div>
                    </div>
                    <div>
                        <div className="d-flex">
                            {job.clientId &&
                                <div style={{ width: '90%' }}>
                                    <ClientsSelect isFromEditJobModal={true} value={job.clientId} onChange={(option) => handleChange({ clientId: option.value })} options={clientOptions} setOptions={setClientOptions} setClientRawName={setClientRawName} />
                                </div>
                            }
                            {job.clientId &&
                                <div style={{ width: '10%' }}>
                                    <div className="d-flex center-xy">
                                        <i title={t('orderForm.viewClientDetails')} onClick={() => setClientInfoModal(true)} className="fa-solid fa-circle-info text-muted cursor-pointer"></i>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </Col>
            </Row>
        </Col>
    )

    const fPickupType = () => (
        <Col lg="6">
            <Form.Group className="mb-2">
                <Form.Label>{t('orderForm.pickupType')} <span className="text-danger">*</span></Form.Label>
                <StyledSelect value={job.pickupType} onChange={(e) => handleChange({ pickupType: e.target.value })} >
                    <option value="" disabled>{t('orderForm.selectPickupType')}</option>
                    <option value="PICKUP_FROM_HOME">{t('orderForm.pickupFromHome')}</option>
                    <option value="CUSTOMER_DELIVERS">{t('orderForm.customerDelivers')}</option>
                </StyledSelect>
            </Form.Group>
        </Col>
    )

    const fPickupTime = () => {
        const isCustomerDelivers = job.pickupType === "CUSTOMER_DELIVERS"
        const fromField = isCustomerDelivers ? 'deliveredByCustomerTime' : 'pickupTime'
        const toField = isCustomerDelivers ? 'deliveredByCustomerTimeTo' : 'pickupTimeTo'
        const label = isCustomerDelivers ? t('orderForm.customerDeliverTime') : t('orderForm.pickupTime')
        return (
            <>
                <Col lg="3">
                    <DatePicker selected={job[fromField] ? new Date(job[fromField]) : null}
                        label={`${label} (${t('orderForm.from')})`}
                        onChange={(date) => handleChange({ [fromField]: date })}
                        onSelect={(date) => handleSelectDate(fromField, date)}
                        showTimeSelect
                        timeIntervals={15}
                        minTime={setHours(setMinutes(new Date(), 0), 5)}
                        maxTime={setHours(setMinutes(new Date(), 59), 23)}
                        timeCaption={t('common.time')}
                        dateFormat="dd.MM.yyyy h:mm aa"
                        className="form-control mb-2"
                        required={job.pickupType === "PICKUP_FROM_HOME" || job.pickupType === "CUSTOMER_DELIVERS"}
                    />
                </Col>
                <Col lg="3">
                    <DatePicker selected={job[toField] ? new Date(job[toField]) : null}
                        label={`${label} (${t('orderForm.to')})`}
                        onChange={(date) => handleChange({ [toField]: date })}
                        onSelect={(date) => handleSelectDate(toField, date)}
                        showTimeSelect
                        timeIntervals={15}
                        minTime={setHours(setMinutes(new Date(), 0), 5)}
                        maxTime={setHours(setMinutes(new Date(), 59), 23)}
                        timeCaption={t('common.time')}
                        dateFormat="dd.MM.yyyy h:mm aa"
                        className="form-control mb-2"
                    />
                </Col>
            </>
        )
    }

    const fDelivery = () => (
        <>
            <Col lg="3">
                <DatePicker selected={job.completionTime ? new Date(job.completionTime) : null}
                    label={t('orderForm.deliveryFrom')} placeholderText=''
                    onChange={(date) => handleChange({ completionTime: date })}
                    onSelect={(date) => handleSelectDate('completionTime', date)}
                    showTimeSelect
                    timeIntervals={15}
                    minTime={setHours(setMinutes(new Date(), 0), 5)}
                    maxTime={setHours(setMinutes(new Date(), 59), 23)}
                    timeCaption={t('common.time')}
                    dateFormat="dd.MM.yyyy h:mm aa"
                    className="form-control"
                />
            </Col>
            <Col lg="3">
                <DatePicker selected={job.completionTimeTo ? new Date(job.completionTimeTo) : null}
                    label={t('orderForm.deliveryTo')} placeholderText=''
                    onChange={(date) => handleChange({ completionTimeTo: date })}
                    onSelect={(date) => handleSelectDate('completionTimeTo', date)}
                    showTimeSelect
                    timeIntervals={15}
                    minTime={setHours(setMinutes(new Date(), 0), 5)}
                    maxTime={setHours(setMinutes(new Date(), 59), 23)}
                    timeCaption={t('common.time')}
                    dateFormat="dd.MM.yyyy h:mm aa"
                    className="form-control"
                />
                {job.status === 'COMPLETED' && job.completionTimeActual &&
                    <p className="text-end text-muted"><small>{t('orderForm.completedAt')} {toLocalDateString(job.completionTimeActual)}</small></p>
                }
            </Col>
        </>
    )

    const fAssignees = () => (
        <>
            <Col lg="4">
                <Form.Group className="mb-2">
                    <AssigneeSelect
                        label={ASSIGNMENT_LABELS.pickup}
                        value={job.pickupAssignedToId ?? ''}
                        assigneesOptions={assigneesOptions}
                        filterRole={'driver'}
                        onChange={(option) => handleChange({ pickupAssignedToId: option?.value })}
                        required={getStatusIndex(job.status) > getStatusIndex(JOB_STATUS.SCHEDULED) &&
                            getStatusIndex(job.status) <= getStatusIndex(JOB_STATUS.IN_PROGRESS) &&
                            job.pickupType === 'PICKUP_FROM_HOME'
                        }
                        isLoading={isLoadingAssigneesOptions}
                    />
                </Form.Group>
            </Col>
            <Col lg="4">
                <Form.Group className="mb-2">
                    <AssigneeSelect
                        label={ASSIGNMENT_LABELS.cleaning}
                        value={job.cleaningAssignedToId ?? ''}
                        assigneesOptions={assigneesOptions}
                        filterRole={'cleaner'}
                        onChange={(option) => handleChange({ cleaningAssignedToId: option?.value })}
                        required={getStatusIndex(job.status) >= getStatusIndex(JOB_STATUS.IN_PROGRESS) &&
                            getStatusIndex(job.status) <= getStatusIndex(JOB_STATUS.FINISHING)}
                        isLoading={isLoadingAssigneesOptions}
                    />
                </Form.Group>
            </Col>
            <Col lg="4">
                <Form.Group className="mb-2">
                    <AssigneeSelect
                        label={ASSIGNMENT_LABELS.delivery}
                        value={job.deliveryAssignedToId ?? ''}
                        assigneesOptions={assigneesOptions}
                        filterRole={'driver'}
                        onChange={(option) => handleChange({ deliveryAssignedToId: option?.value })}
                        required={getStatusIndex(job.status) >= getStatusIndex(JOB_STATUS.READY)}
                        isLoading={isLoadingAssigneesOptions}
                    />
                </Form.Group>
            </Col>
        </>
    )

    const fPayment = () => (
        <>
            <Col lg="3" style={{ marginRight: '24px', width: 'auto', flex: '0 0 auto' }}>
                <Form.Group className="mb-2">
                    <Form.Label>{t('orderForm.paymentStatus')}</Form.Label>
                    <div className="pay-toggle">
                        <button type="button" className={`pending${job.paymentStatus !== "PAID" ? ' on' : ''}`} onClick={() => handlePaymentStatusChange(false)}>{t('orderForm.pending')}</button>
                        <button type="button" className={`paid${job.paymentStatus === "PAID" ? ' on' : ''}`} onClick={() => handlePaymentStatusChange(true)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            {t('orderForm.paid')}
                        </button>
                    </div>
                </Form.Group>
            </Col>
            <Col lg="3">
                <DatePicker selected={job.paymentDueDate ? new Date(job.paymentDueDate) : null}
                    label={<>{t('orderForm.paymentDue')}{isPaymentOverdue(job) && <span className="badge bg-danger ms-1" style={{ fontSize: '10px', verticalAlign: 'middle' }}>{t('orderForm.overdue')}</span>}</>}
                    placeholderText={job.paymentStatus === 'PAID' ? '' : t('orderForm.setOnCompletion')}
                    onChange={(date) => handleChange({ paymentDueDate: date || null })}
                    dateFormat="dd.MM.yyyy"
                    isClearable
                    className="form-control mb-2"
                />
            </Col>
            {job.paymentStatus === "PAID" &&
                <>
                    <Col lg="3">
                        <Form.Group className="mb-2">
                            <Form.Label>{t('orderForm.paymentMethod')} <span className="text-danger">*</span></Form.Label>
                            <StyledSelect compact placeholder={t('styledSelect.placeholder')} value={job.paymentMethod ?? ""} onChange={(e) => handleChange({ paymentMethod: e.target.value })} required>
                                <option value="" disabled>{t('orderForm.selectPaymentMethod')}</option>
                                <option value="CASH">{t('orderForm.cash')}</option>
                                <option value="CREDIT_CARD">{t('orderForm.creditCard')}</option>
                                <option value="CHECK">{t('orderForm.check')}</option>
                            </StyledSelect>
                        </Form.Group>
                    </Col>
                    <Col lg="3">
                        <DatePicker selected={job.paymentTime ? new Date(job.paymentTime) : null}
                            label={t('orderForm.paymentTime')} placeholderText=''
                            onChange={(date) => handleChange({ paymentTime: date })}
                            onSelect={(date) => handleSelectDate('paymentTime', date)}
                            showTimeSelect
                            timeIntervals={15}
                            minTime={setHours(setMinutes(new Date(), 0), 5)}
                            maxTime={setHours(setMinutes(new Date(), 59), 23)}
                            timeCaption={t('common.time')}
                            dateFormat="dd.MM.yyyy h:mm aa"
                            className="form-control mb-2"
                            required
                        />
                    </Col>
                    <Col lg="3">
                        <Form.Group className="mb-2">
                            <Form.Label>{t('orderForm.paymentType')} <span className="text-danger">*</span></Form.Label>
                            <StyledSelect compact placeholder={t('styledSelect.placeholder')} value={job.paymentType ?? ""} onChange={(e) => handleChange({ paymentType: e.target.value })} required>
                                <option value="" disabled>{t('orderForm.selectPaymentType')}</option>
                                <option value="BEFORE_SERVICE">{t('orderForm.beforeService')}</option>
                                <option value="DURING_SERVICE">{t('orderForm.duringService')}</option>
                                <option value="AFTER_SERVICE">{t('orderForm.afterService')}</option>
                            </StyledSelect>
                        </Form.Group>
                    </Col>
                </>
            }
        </>
    )

    const fNotes = () => (
        <Col lg="12" className="mb-3">
            <FormInput as="textarea" label={t('orderForm.notes')} value={job.note ?? ""} onChange={(e) => handleChange({ note: e.target.value })} />
        </Col>
    )

    const hasLineItems = job.lineItemsTemp?.length > 0

    const liHeadInner = () => (
        <span className="rf-group">
            {jobId && hasLineItems &&
                <span className="rf" onClick={() => setInvoiceToSend({
                    jobId,
                    invoiceNumber: invoice?.invoiceNumber,
                    clientName: [clientStats?.client?.firstName, clientStats?.client?.lastName].filter(Boolean).join(' ') || t('orderForm.client'),
                    email: clientStats?.client?.email || '',
                    phone: clientStats?.client?.phone || '',
                })}>
                    <i className="fa-solid fa-envelope"></i> {t('orderForm.sendInvoice')}
                </span>
            }
            {jobId && hasLineItems &&
                <span className={`rf ${isPrinting ? 'no-pointer-events' : ''}`} onClick={() => handlePrint()}>
                    <i className="fa-solid fa-receipt"></i> {`${isPrinting || isFetchingSaleInvoice ? t('orderForm.preparing') : t('orderForm.printInvoice')}`}
                </span>
            }
            <span className="rf" onClick={() => queryClient.invalidateQueries(['available-badges'])}>
                <i className={`fa-solid fa-arrows-rotate ${isAvailableBadgesFetching ? 'fa-spin' : ''}`}></i> {t('orderForm.refreshTags')}
            </span>
        </span>
    )

    const fLineItemsCard = () => (
        <Col xs="12">
            <div className="li-wrap">
                <div className="li-head">
                    <span className="t">{t('orderForm.lineItems')}</span>
                    {liHeadInner()}
                </div>
                <div className="li-picker">
                    {job?.lineItemsTemp?.length > 0
                        ? job.lineItemsTemp.map((lineItem, index) => renderItemCard(lineItem, index))
                        : <p><i>{t('orderForm.noLineItems')}</i></p>}
                    <button type="button" className="li-pick add" onClick={() => handleAddLineItem()}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        {t('orderForm.addService')}
                    </button>
                    <button type="button" className="li-pick add" onClick={() => handleAddMaterialLineItem()}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        </svg>
                        {t('orderForm.addMaterial')}
                    </button>
                </div>
                <p className="li-pick-hint">{t('orderForm.tapToEdit')}</p>
            </div>
        </Col>
    )

    /* one job-note entry in the timeline — note text, photos, when, and who
       added it (their photo, initials on their own when there is no avatar) */
    const renderNoteEntry = (note, key) => {
        // Defensive: a system-written note has no author, and `photos` is only
        // an array by convention on the API side.
        const actor = note.createdBy?.fullName ?? t('orderForm.system')
        const photos = Array.isArray(note.photos) ? note.photos : []
        const what = note.body
            ? t('orderForm.addedNote')
            : t('orderForm.addedPhotos', { count: photos.length })
        return (
            <div className="jact-item jact-item-note" key={key}>
                <Avatar className="jact-dot" size={28} name={actor} src={note.createdBy?.avatarUrl} />
                <div className="jact-content">
                    <div className="jact-line">
                        <span className="actor">{actor}</span> {what}
                        {note.visibleToCustomer &&
                            <span className="chip" title={t('orderForm.sharedWithCustomer')}
                                style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 800, background: '#DCFCE7', color: '#15803D', border: 0 }}>
                                {t('orderForm.sharedWithCustomer')}
                            </span>
                        }
                        <span className="jact-time">{toActivityDateString(note.createdAt)}</span>
                    </div>
                    {note.body && <div className="jact-note">{note.body}</div>}
                    {photos.length > 0 &&
                        <div className="jact-photos">
                            {photos.map((url, pi) => (
                                <button type="button" className="jact-photo" key={`p-${pi}`} title={t('orderForm.viewPhoto')}
                                    onClick={() => setPhotoToView(url)}>
                                    <img src={url} alt={t('orderForm.attachmentN', { n: pi + 1 })} />
                                </button>
                            ))}
                        </div>
                    }
                </div>
            </div>
        )
    }

    const fJobExtra = () => (
        <div className="job-extra">
            {/* Mobile reaches Delete from the Status step instead (see
                renderWizardStep) — this block is the last thing in a six-step
                wizard, which is no place for it. Rendering it here too would
                give the mobile layout two Delete buttons. */}
            {!isMobile &&
                <>
                    <div className='d-flex justify-content-start'>
                        <Button type="button" onClick={() => setDeleteJobConfirmation(true)} className="me-2" variant="outline-secondary">
                            <i className="fa-solid fa-trash text-muted"></i> {t('orderForm.deleteJob')}
                        </Button>
                    </div>

                    <hr />
                </>
            }
            {jobId && !isMobile && <JobDocumentsSection jobId={jobId} job={job} client={clientStats?.client} onSendTracking={openTrackingSend} />}

            <div className="jact">
                <button type="button" className="jact-toggle" onClick={() => setShowJobActivities(!showJobActivities)}>
                    <span className="lhs"><i className="fa-solid fa-clock-rotate-left"></i> {t('orderForm.jobActivity')}</span>
                    <svg className={`chev${showJobActivities ? ' open' : ''}`} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                </button>

                {/* Sits outside the Collapse on purpose: documenting a job (a
                    photo of a damaged carpet, say) has to be one tap away, not
                    behind expanding the history first. Desktop only — on mobile
                    this whole block is the last wizard step, five taps from the
                    top, so the composer is rendered on the Status step instead
                    (see renderWizardStep) and is left out here to keep exactly
                    one composer per layout. */}
                {jobId && !isMobile && <JobNoteComposer jobId={jobId} />}

                <Collapse in={showJobActivities}>
                    <div className="jact-body">
                        {groupedJobActivities.length > 0
                            ? groupedJobActivities.map((activitiesByDate, gi) => (
                                <div key={`g-${gi}`}>
                                    <div className="jact-day"><span>{activityHeaderText(activitiesByDate.createdAtDate)}</span></div>
                                    <div className="jact-timeline">
                                        {activitiesByDate.activities.map((activity, ai) => {
                                            if (activity._kind === 'notification') {
                                                return (
                                                    <div className="jact-item jact-item-notification" key={`n-${gi}-${ai}`}>
                                                        <span className="jact-dot">🔔</span>
                                                        <div className="jact-content">
                                                            <div className="jact-line">
                                                                {NOTIFICATION_TITLES[activity.message]}
                                                                <span className="jact-time">{toActivityDateString(activity.createdAt)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            }
                                            if (activity._kind === 'note') {
                                                return renderNoteEntry(activity, `nt-${gi}-${ai}`)
                                            }
                                            const actor = activity.createdBy?.fullName ?? t('orderForm.system')
                                            const objectLabels = activity.message.object === 'Job' ? jobLabel : lineItemLabel
                                            const updates = activity.message.updates || []
                                            return (
                                                <div className="jact-item" key={`a-${gi}-${ai}`}>
                                                    {/* Who did it, as their profile photo when the API
                                                        has one — Avatar falls back to initials on its
                                                        own, so this is correct before and after the
                                                        activity author select starts returning avatarUrl. */}
                                                    <Avatar className="jact-dot" size={28} name={actor} src={activity.createdBy?.avatarUrl} />
                                                    <div className="jact-content">
                                                        <div className="jact-line">
                                                            <span className="actor">{actor}</span> {activityVerb(activity.message)}
                                                            <span className="jact-time">{toActivityDateString(activity.createdAt)}</span>
                                                        </div>
                                                        {updates.length > 0 &&
                                                            <div className="jact-changes">
                                                                {updates.map((u, ui) => {
                                                                    const label = objectLabels[u.prop] || toTitleCase(u.prop)
                                                                    const fromCls = getVariantIfStatusProp(u.prop, u.from) || ''
                                                                    const toCls = getVariantIfStatusProp(u.prop, u.to) || ''
                                                                    const fromVal = formatValByProp(u.from, u.prop)
                                                                    const toVal = formatValByProp(u.to, u.prop)
                                                                    const has = (x) => x !== undefined && x !== null && x !== ''
                                                                    const skipped = u.prop === 'status' ? getSkippedStages(u.from, u.to) : []
                                                                    return (
                                                                        <div className="jact-change" key={`u-${ui}`}>
                                                                            <span className="field">{label}</span>
                                                                            <span className={`val ${fromCls}`}>{has(fromVal) ? fromVal : '—'}</span>
                                                                            <svg className="arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                                                                            <span className={`val ${toCls}`}>{has(toVal) ? toVal : '—'}</span>
                                                                            {skipped.length > 0 && <span className="wf-skipped">{` (${t('orderForm.skippedStages', { stages: skipped.join(', ') })})`}</span>}
                                                                        </div>
                                                                    )
                                                                })}
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            ))
                            : <p className="text-muted mb-0"><small>{t('orderForm.noActivityYet')}</small></p>}
                    </div>
                </Collapse>
            </div>
        </div>
    )

    /* ── mobile wizard: full inline line-item editor (no side pane) ── */
    const renderItemEditorInline = (lineItem, index) => {
        const disabled = lineItem?.service?.disabled || lineItem?.material?.disabled
        const isMat = lineItem.type === 'MATERIAL'
        return (
            <div key={`m-li-${index}`} className="m-li-card">
                <div className="m-li-top">
                    <span className="m-li-no">
                        {isMat
                            ? (lineItem.materialName || lineItem.material?.name || (lineItem.materialId ? t('orderForm.selectedMaterial') : t('orderForm.itemN', { n: index + 1 })))
                            : (lineItem.serviceName || lineItem.service?.name || (lineItem.serviceId ? t('orderForm.selectedService') : t('orderForm.itemN', { n: index + 1 })))}
                        {disabled && <i className="fa-solid fa-triangle-exclamation text-danger ms-1" title={t('common.disabled')}></i>}
                    </span>
                    <button type="button" className="li-dup" title={t('orderForm.duplicateItem')} onClick={() => handleDuplicateLineItem(index)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                    </button>
                    <button type="button" className="li-del" title={t('orderForm.removeItem')} onClick={() => handleShowConfirmation(lineItem, index)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                    </button>
                </div>
                <div className="li-grid">
                    <div className="li-col-2">
                        {isMat ? (
                            <Form.Group>
                                <Form.Label>{t('orderForm.material')} <span className="text-danger">*</span></Form.Label>
                                <MaterialSelect materialId={lineItem?.materialId} required value={lineItem.materialId ?? ''} onChange={(option) => handleMaterialSelect(option, index)} isDisabled={disabled} />
                            </Form.Group>
                        ) : (
                            <Form.Group>
                                <Form.Label>{t('orderForm.service')} <span className="text-danger">*</span></Form.Label>
                                <ServiceSelect serviceId={lineItem?.serviceId} required value={lineItem.serviceId ?? ''} onChange={(option) => handleServiceSelect(option, index)} isDisabled={disabled} />
                            </Form.Group>
                        )}
                    </div>
                    <div className="li-col-2">
                        <Form.Group>
                            <Form.Label>{t('orderForm.tagNumber')} {isCleaningAndOnwards(job) && <span className="text-danger">*</span>}</Form.Label>
                            <InputGroup className="input-group-react-select">
                                <BadgeSelect required={isCleaningAndOnwards(job)} value={lineItem.badgeId ?? ''}
                                    isLoading={isAvailableBadgesLoading || isLoadingLineItemBadgeIds}
                                    options={sortBadgeOptions(allBadgeOptions.filter(x => !selectedBadges.includes(x.value) || x.value === lineItem.badgeId))}
                                    onChange={(option) => handleLineItemChange({ badgeId: option.value }, index)} isDisabled={disabled} />
                            </InputGroup>
                            {renderNextTagChip(lineItem, index, disabled)}
                        </Form.Group>
                    </div>
                    {lineItem.unit === 'm2' &&
                        <>
                            <div>
                                <FormInput value={lineItem.width ?? ''} label={t('orderForm.width')} type="number" min={0.01} step="0.01"
                                    onChange={(e) => handleLineItemChange({ width: e.target.value }, index)}
                                    required={isCleaningCompletedAndOnwards(job)} disabled={disabled} />
                            </div>
                            <div>
                                <FormInput value={lineItem.length ?? ''} label={t('orderForm.length')} type="number" min={0.01} step="0.01"
                                    onChange={(e) => handleLineItemChange({ length: e.target.value }, index)}
                                    required={isCleaningCompletedAndOnwards(job)} disabled={disabled} />
                            </div>
                        </>
                    }
                    <div>
                        <FormInput value={lineItem.quantity} title={rsdFormat(+lineItem.quantity, false)}
                            label={(lineItem.serviceId || lineItem.materialId) ? lineItem.unit : t('orderForm.quantity')}
                            type="number" min={getLineItemMinQuantity(lineItem.unit)} step={lineItem.unit === 'Piece' ? 1 : 0.01}
                            onChange={(e) => handleLineItemChange({ quantity: e.target.value }, index)}
                            disabled={lineItem.unit === 'm2' || disabled} required />
                    </div>
                    <div>
                        <FormInput value={lineItem.taxRate} label={t('orderForm.taxPercent')} type="number" step='0.01'
                            onChange={(e) => handleLineItemChange({ taxRate: e.target.value }, index)} disabled={disabled} />
                    </div>
                    <div>
                        <FormInput disabled value={lineItem.price} title={rsdFormat(+lineItem.price, false)}
                            label={t('orderForm.price')} type="number" min="0.01" step="0.01"
                            onChange={(e) => handleLineItemChange({ price: e.target.value }, index)} required />
                    </div>
                    <div>
                        <FormInput disabled value={lineItem.priceWithTax} title={rsdFormat(+lineItem.priceWithTax, false)}
                            label={t('orderForm.total')} type="number" min="0.01" step="0.01" required
                            onChange={(e) => handleLineItemChange({ priceWithTax: e.target.value }, index)} />
                    </div>
                </div>
                <div className="m-li-total"><span>{t('orderForm.lineTotal')}</span><b>{rsdFormat(+lineItem.priceWithTax, false)} RSD</b></div>
            </div>
        )
    }

    /* ── mobile wizard step body ── */
    const renderWizardStep = () => {
        if (wizStep === 0) return <Row className="mb-3">{fClient()}{renderClientSummary()}{fPickupType()}{fPickupTime()}</Row>
        if (wizStep === 1) return (
            <div className="m-items">
                <div className="li-head">
                    <span className="t">{t('orderForm.lineItems')}</span>
                    {liHeadInner()}
                </div>
                {job?.lineItemsTemp?.length > 0
                    ? job.lineItemsTemp.map((lineItem, index) => renderItemEditorInline(lineItem, index))
                    : <p><i>{t('orderForm.noLineItems')}</i></p>}
                <button type="button" className="li-pick add" onClick={() => handleAddLineItem()}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    {t('orderForm.addService')}
                </button>
                <button type="button" className="li-pick add" onClick={() => handleAddMaterialLineItem()}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    </svg>
                    {t('orderForm.addMaterial')}
                </button>
            </div>
        )
        if (wizStep === 2) return (
            <>
                <Form.Label>{t('orderForm.status')} <span className="text-danger">*</span></Form.Label>
                <StatusSelector value={job.status} onChange={(status) => handleChange({ status: status })}
                    cancelledFromStatus={cancelledFromStatus} onResume={askResumeJob} />
                {/* Saying what stage the job is at and saying what happened to
                    it are the same moment in the field — a carpet that arrives
                    damaged gets photographed here, two taps in, rather than at
                    the end of the wizard. Same component as desktop, just moved
                    up; the activity history itself stays on the Review step. */}
                {jobId &&
                    <>
                        <Form.Label className="mt-3">{t('orderForm.addToJobActivity')}</Form.Label>
                        <JobNoteComposer jobId={jobId} />
                        <JobDocumentsSection jobId={jobId} job={job} client={clientStats?.client} onSendTracking={openTrackingSend} />
                    </>
                }
                {/* Job-level actions. On desktop Cancel sits in the summary pane
                    and Delete below the form, both permanently on screen. The
                    mobile wizard has no summary pane and renders fJobExtra only
                    on the LAST step, which put Delete five taps away and left
                    Cancel out entirely — so both live here, next to the status
                    they act on. Resume is not repeated: it is already in the
                    StatusSelector above whenever the job is cancelled. */}
                {jobId &&
                    <div className="m-job-actions">
                        {!isCancelled(job.status) &&
                            <button type="button" className="btn-sbr btn-sm-sbr btn-danger-sbr" onClick={askCancelJob}>
                                <i className="fa-solid fa-ban"></i> {t('orderForm.cancelJob')}
                            </button>
                        }
                        <button type="button" className="btn-sbr btn-sm-sbr btn-ghost-sbr" onClick={() => setDeleteJobConfirmation(true)}>
                            <i className="fa-solid fa-trash"></i> {t('orderForm.deleteJob')}
                        </button>
                    </div>
                }
            </>
        )
        if (wizStep === 3) return <Row className="mb-3">{fAssignees()}</Row>
        if (wizStep === 4) return <Row className="mb-3 g-4">{fDelivery()}{fPayment()}</Row>
        return (
            <>
                <div className="job-totals" style={{ marginTop: 0 }}>
                    <div className="tl"><span>{t('orderForm.items')}</span><span>{job.lineItemsTemp?.length || 0}</span></div>
                    <div className="grand"><span className="l">{t('orderForm.grandTotal')}</span><span className="v">{getTotalPrice()}</span></div>
                </div>
                <Row className="mb-3 mt-3">{fNotes()}</Row>
                {fJobExtra()}
            </>
        )
    }

    /* ── footer: wizard nav on mobile, standard actions on desktop ── */
    const footerNode = isMobile ? (
        <div className="m-wiz-foot">
            {wizStep > 0
                ? <button key="wiz-back" type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => goWiz(-1)} disabled={isLoading}>{t('common.back')}</button>
                : <button key="wiz-cancel" type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide} disabled={isLoading}>{t('common.cancel')}</button>}
            {wizStep < WIZ_STEPS.length - 1
                ? <button key="wiz-next" type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => goWiz(1)}>{t('orderForm.continue')}</button>
                : <button key="wiz-submit" type="submit" form="edit-job" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading}>
                    {isLoading ? t('orderForm.savingEllipsis') : t('orderForm.saveChanges')}
                </button>}
        </div>
    ) : (
        <>
            <div className="drawer-total">
                <span className="l">{t('common.total')}</span>
                <span className="v">{getTotalPrice()}</span>
            </div>
            <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide} disabled={isLoading}>{t('common.cancel')}</button>
            <button type='submit' form="edit-job" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading}>
                {isLoading ? t('orderForm.savingEllipsis') : t('orderForm.saveChanges')}
            </button>
        </>
    )

    return (
        <>
            <div ref={invoiceRef} className="hide-on-screen">
                <SalesInvoicePrint invoice={invoice} />
            </div>
            <InvoiceSharePanel invoice={invoiceToSend} onClose={() => setInvoiceToSend(null)} />
            {customerPromptStage && jobId &&
                <CustomerMessagePrompt jobId={jobId} stage={customerPromptStage}
                    client={clientStats?.client}
                    onClose={() => { setCustomerPromptStage(null); if (!trackingPromptManual) onHide(); setTrackingPromptManual(false); }} />
            }

            <ConfirmationModal display={deleteJobConfirmation} toggle={setDeleteJobConfirmation} title={t('orderForm.confirmDeleteTitle')}
                message={t('orderForm.confirmDeleteJob')} confirmAction={handleDeleteJob}
            />
            <ConfirmationModal display={cancelJobConfirmation} toggle={setCancelJobConfirmation} title={t('orderForm.confirmCancelTitle')}
                message={t('orderForm.cancelConfirmMessage')}
                confirmAction={handleCancelJob} confirmText={t('orderForm.cancelJob')} cancelText={t('orderForm.keepJob')}
            >
                <div className="field">
                    <label htmlFor="cancel-reason">{t('orderForm.reasonForCancelling')}</label>
                    <textarea id="cancel-reason" className="form-control" rows={3} maxLength={5000}
                        value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                        placeholder={t('orderForm.cancelPlaceholder')} />
                    <span className="hint">{t('orderForm.cancelReasonHint')}</span>
                </div>
            </ConfirmationModal>
            <ConfirmationModal display={resumeJobConfirmation} toggle={setResumeJobConfirmation} title={t('orderForm.confirmResumeTitle')}
                message={t('orderForm.resumeConfirmMessage', { status: getStatusLabel(resumeToStatus || JOB_STATUS.NEW) })}
                confirmAction={handleResumeJob}
                confirmText={t('orderForm.resumeJob')} cancelText={t('orderForm.keepCancelled')} tone='primary'
            />
            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal} title={t('orderForm.deleteLineItem')}
                message={t('orderForm.confirmDeleteLineItem')} confirmAction={handleDeleteLineItem}
            />
            <SetLineItemModal display={setLineItemModal} toggle={setSetLineItemModal} lineItemToEdit={lineItemToEdit} jobId={jobId} />
            <SetClientModal display={setClientModal} toggle={setSetClientModal} onCreateClient={onCreateClient} suggestName={clientRawName} asDrawer />
            {job.clientId &&
                <SetClientModal display={editClientModal} toggle={setEditClientModal} clientId={job.clientId} onCreateClient={onUpdateClient} asDrawer />
            }
            <ClientInfoModal display={clientInfoModal} toggle={setClientInfoModal} clientId={job.clientId} onViewJobs={onHide} />
            <JobNotePhotoModal src={photoToView} onClose={() => setPhotoToView(null)} />

            <SbrDrawer
                show={display}
                onShow={onShow}
                onHide={onHide}
                // The customer message prompt and the invoice share panel render
                // outside this drawer, so the Offcanvas focus trap has to be
                // released while either is open or their fields cannot be typed in.
                enforceFocus={!customerPromptStage && !invoiceToSend}
                backdrop="static"
                className={`job-drawer${isMobile ? ' job-drawer-wiz' : ''}`}
                title={t('orderForm.editJobTitle', { number: jobId })}
                subtitle={t('orderForm.editSubtitle')}
                icon={<ClipboardIcon />}
                footer={footerNode}
            >

                    {alert &&
                        <Alert key="danger" variant="danger" onClose={() => setAlert()} dismissible>
                            {t('orderForm.errorAlert')} <span className="text-bold">{alert}</span>
                        </Alert>
                    }

                    <Form id='edit-job' onSubmit={handleSubmit}>
                        {isMobile ? (
                            <div className="m-wiz">
                                <div className="m-wiz-bar">
                                    {WIZ_STEPS.map((s, i) => <span key={i} className={`m-wiz-seg${i < wizStep ? ' done' : ''}${i === wizStep ? ' on' : ''}`} />)}
                                </div>
                                <div className="m-wiz-cap">{t('orderForm.step', { current: wizStep + 1, total: WIZ_STEPS.length })}</div>
                                <h2 className="m-wiz-h">{WIZ_STEPS[wizStep]}</h2>
                                {renderWizardStep()}
                            </div>
                        ) : (
                            <div className="job-split">
                                <div className="job-split-left">
                                    <Row className="mb-3 g-4">
                                        {fClient()}{renderClientSummary()}{fPickupType()}{fPickupTime()}{fDelivery()}{fAssignees()}{fPayment()}{fNotes()}{fLineItemsCard()}
                                    </Row>
                                    {fJobExtra()}
                                </div>
                                <div className="job-split-right">
                                    {detailIndex == null ? renderSummary() : renderItemEditor(detailIndex)}
                                </div>
                            </div>
                        )}
                    </Form>

                    {(isFetching || isAvailableBadgesLoading || isLoadingLineItemBadgeIds || isDeletingLineItem || isLoadingAssigneesOptions || isDeletingJob) &&
                        <OverlayLoader />
                    }

            </SbrDrawer>


        </>
    )
}

export default SetTempEditOrderModal
